import { useState } from 'react'
import { logger } from '@/utils/logger'
import { toast } from 'sonner'
import { MapPin, Loader2, CheckCircle, XCircle, LocateFixed, ArrowLeft } from 'lucide-react'
import { m, AnimatePresence } from 'framer-motion'
import { useDeliveryStore } from '@/stores'
import { useDeliveryCheck } from '@/api_communication/hooks'
import { useTranslation } from 'react-i18next'

export const DeliveryAddressChecker = ({ onVerified }: { onVerified?: () => void }) => {
  const { t } = useTranslation()
  const [city, setCity] = useState('')
  const [street, setStreet] = useState('')
  const [building, setBuilding] = useState('')
  const [flat, setFlat] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [step, setStep] = useState<'input' | 'confirm'>('input')
  const [errorMessage, setErrorMessage] = useState('')
  const [distanceKm, setDistanceKm] = useState<number | null>(null)
  const [cost, setCost] = useState<number>(0)
  const [coords, setCoords] = useState<{lat: number | null, lon: number | null}>({ lat: null, lon: null })
  const [lastAutoCheckedAddress, setLastAutoCheckedAddress] = useState('')
  const [confirmAfterCurrentCheck, setConfirmAfterCurrentCheck] = useState(false)
  
  const { setDeliveryAddress, deliveryZoneId, minOrderValue, freeDeliveryFrom } = useDeliveryStore()
  const deliveryCheck = useDeliveryCheck()
  const isLoading = deliveryCheck.isPending

  const formatPostalCode = (value: string) => {
    // Strip non-digits and limit to 5
    const digits = value.replace(/\D/g, '').slice(0, 5);
    // Format as XX-XXX
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  };

  const getFullAddress = () =>
    `${street} ${building}${flat ? `/${flat}` : ''}, ${postalCode} ${city}`.trim()

  const hasRequiredFields = () =>
    !!street.trim() && !!city.trim() && !!postalCode.trim() && !!building.trim()

  const handleCheck = (autoConfirm = false) => {
    if (!street.trim() || !city.trim() || !postalCode.trim()) return
    setStatus('idle')
    setErrorMessage('')
    setDistanceKm(null)
    setCoords({ lat: null, lon: null })
    
    logger.addBreadcrumb({ category: 'delivery', message: 'Checking delivery address availability', data: { address: getFullAddress() } })
    deliveryCheck.mutate(getFullAddress(), {
      onSuccess: (data) => {
        logger.addBreadcrumb({ category: 'delivery', message: 'Address check result', data: { available: data.available, distance: data.distance_km, cost: data.cost } })
        setStatus(data.available ? 'success' : 'error')
        if (!data.available && data.message) setErrorMessage(data.message)
        if (data.distance_km !== undefined) setDistanceKm(data.distance_km)
        if (data.cost !== undefined) setCost(data.cost)
        if (data.lat && data.lon) setCoords({ lat: data.lat, lon: data.lon })
        if (autoConfirm || confirmAfterCurrentCheck) {
          setStep('confirm')
          setConfirmAfterCurrentCheck(false)
        }
      },
      onError: (err: any) => { 
        logger.captureException(err, { type: 'delivery_check_failed', address: getFullAddress() })
        setConfirmAfterCurrentCheck(false) 
      },
    })
  }

  const handleNextStep = () => {
    if (!street.trim() || !city.trim() || !postalCode.trim()) {
      toast.error('Uzupełnij miasto, kod pocztowy i ulicę.')
      return
    }
    if (isLoading) { setConfirmAfterCurrentCheck(true); return }
    if (status === 'success' || status === 'error') setStep('confirm')
    else handleCheck(true)
  }

  const handleFinalConfirm = () => {
    logger.addBreadcrumb({ category: 'delivery', message: 'User confirmed delivery address final step' })
    setDeliveryAddress(
      street,
      city,
      building,
      flat,
      postalCode,
      distanceKm,
      coords.lat,
      coords.lon,
      cost,
      deliveryZoneId,
      minOrderValue,
      freeDeliveryFrom,
      status === 'success'
    )
    if (onVerified) onVerified()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleCheck()
  }

  const handleAutoCheckOnBlur = () => {
    if (!hasRequiredFields() || isLoading) return
    const fullAddress = getFullAddress()
    if (!fullAddress || fullAddress === lastAutoCheckedAddress) return
    setLastAutoCheckedAddress(fullAddress)
    handleCheck(false)
  }

  const handleGeolocation = () => {
    if (!navigator.geolocation) {
      toast.error('Twoja przeglądarka nie obsługuje geolokalizacji.')
      return
    }
    const toastId = toast.loading('Pobieranie lokalizacji...')
    logger.addBreadcrumb({ category: 'delivery', message: 'User requested geolocation' })
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          logger.addBreadcrumb({ category: 'delivery', message: 'Geolocation success', data: { lat: latitude, lon: longitude } })
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
            { headers: { 'Accept-Language': 'pl', 'User-Agent': 'SushiKing-Frontend/1.0' } }
          );
          if (!response.ok) throw new Error('Błąd API lokalizacji');
          const data = await response.json();
          const addr = data.address || {};
          const cityName = addr.city || addr.town || addr.village || addr.municipality || addr.county || '';
          const road = addr.road || addr.pedestrian || addr.square || addr.neighbourhood || addr.suburb || '';
          const house = addr.house_number || '';
          setCity(cityName);
          setStreet(road);
          setBuilding(house);
          setPostalCode(formatPostalCode(addr.postcode || ''));
          setFlat('');
          setStatus('idle');
          toast.success('Lokalizacja pobrana. Możesz uzupełnić dane.', { id: toastId });
        } catch (err) {
          logger.captureException(err, { type: 'geolocation_reverse_geocode_failed' })
          toast.error('Nie udało się przetworzyć koordynatów na adres.', { id: toastId });
        }
      },
      (error) => {
        logger.addBreadcrumb({ category: 'delivery', message: 'Geolocation permission/error', level: 'warning', data: { code: error.code, message: error.message } })
        let msg = 'Nie udało się pobrać lokalizacji.';
        if (error.code === error.PERMISSION_DENIED)
          msg = 'Brak uprawnień do lokalizacji. Odblokuj dostęp w przeglądarce.';
        else if (error.code === error.TIMEOUT)
          msg = 'Przekroczono czas oczekiwania na lokalizację.';
        toast.error(msg, { id: toastId });
      },
      { timeout: 15000, maximumAge: 60000 }
    )
  }

  // Shared input style helper
  const inputClass = (hasBorder = false) =>
    `w-full h-14 px-5 rounded-[1.25rem] border bg-white/40 transition-all duration-300 focus:outline-none text-[15px] font-bold text-black placeholder:text-black/30 backdrop-blur-md ${
      status === 'error'
        ? 'border-red-400/50 focus:border-red-500 shadow-[0_0_12px_rgba(239,68,68,0.1)]'
        : status === 'success'
        ? 'border-green-400/50 focus:border-green-500 shadow-[0_0_12px_rgba(34,197,94,0.1)]'
        : 'border-white/80 hover:bg-white/70 focus:bg-white focus:border-white focus:shadow-[0_8px_20px_-6px_rgba(0,0,0,0.1)]'
    }${hasBorder ? ' col-span-2' : ''}`

  return (
    <div className="w-full">
      <AnimatePresence mode="wait">
        {step === 'input' ? (
          <m.div
            key="input-step"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="space-y-4"
          >
            {/* Geolocation button */}
            <div className="flex justify-end">
              <button
                onClick={handleGeolocation}
                type="button"
                className="inline-flex items-center gap-2 px-4 py-2 mt-2 -mb-2 text-[12px] font-bold tracking-wide text-black/50 hover:text-black hover:bg-white/60 bg-white/30 rounded-[1rem] shadow-sm transition-all duration-300 active:scale-95"
                title={t('uzyjMojejLokalizacji')}
              >
                <LocateFixed className="h-4 w-4" />
                Użyj mojej lokalizacji
              </button>
            </div>

            {/* Form grid */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:px-4">
              {/* City – full width */}
              <div className="col-span-2 relative drop-shadow-sm">
                <MapPin className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-black/30 pointer-events-none" />
                <input
                  type="text"
                  value={city}
                  onChange={(e) => { setCity(e.target.value); if (status !== 'idle') setStatus('idle') }}
                  onKeyDown={handleKeyDown}
                  onBlur={handleAutoCheckOnBlur}
                  placeholder="Miasto"
                  className={`w-full h-14 pl-12 pr-5 rounded-[1.25rem] border bg-white/40 backdrop-blur-md transition-all duration-300 focus:outline-none text-[15px] font-bold text-black placeholder:text-black/30 ${
                    status === 'error'
                      ? 'border-red-400/50 focus:border-red-500 shadow-[0_0_12px_rgba(239,68,68,0.1)]'
                      : status === 'success'
                      ? 'border-green-400/50 focus:border-green-500 shadow-[0_0_12px_rgba(34,197,94,0.1)]'
                      : 'border-white/80 hover:bg-white/70 focus:bg-white focus:border-white focus:shadow-[0_8px_20px_-6px_rgba(0,0,0,0.1)]'
                  }`}
                />
              </div>

              {/* Postal code */}
              <div className="drop-shadow-sm">
                <input
                  type="text"
                  value={postalCode}
                  onChange={(e) => { setPostalCode(formatPostalCode(e.target.value)); if (status !== 'idle') setStatus('idle') }}
                  onKeyDown={handleKeyDown}
                  onBlur={handleAutoCheckOnBlur}
                  placeholder="Kod pocztowy"
                  className={inputClass()}
                />
              </div>

              {/* Street */}
              <div className="drop-shadow-sm">
                <input
                  type="text"
                  value={street}
                  onChange={(e) => { setStreet(e.target.value); if (status !== 'idle') setStatus('idle') }}
                  onKeyDown={handleKeyDown}
                  onBlur={handleAutoCheckOnBlur}
                  placeholder="Ulica"
                  className={inputClass()}
                />
              </div>

              {/* Building */}
              <div className="drop-shadow-sm">
                <input
                  type="text"
                  value={building}
                  onChange={(e) => { setBuilding(e.target.value); if (status !== 'idle') setStatus('idle') }}
                  onKeyDown={handleKeyDown}
                  onBlur={handleAutoCheckOnBlur}
                  placeholder="Numer budynku"
                  className={inputClass()}
                />
              </div>

              {/* Flat */}
              <div className="drop-shadow-sm">
                <input
                  type="text"
                  value={flat}
                  onChange={(e) => { setFlat(e.target.value); if (status !== 'idle') setStatus('idle') }}
                  onKeyDown={handleKeyDown}
                  placeholder="Lokal (opcjonalnie)"
                  className={inputClass()}
                />
              </div>
            </div>

            {/* Submit button */}
            <div className="lg:px-4 pt-2">
              <button
                onClick={handleNextStep}
                disabled={isLoading}
                className="w-full h-14 bg-gradient-to-r from-red-600 to-red-500 text-white text-[14px] font-black uppercase tracking-[0.15em] rounded-[1.25rem] hover:from-red-500 hover:to-red-400 active:scale-95 transition-all duration-300 shadow-[0_8px_20px_rgba(239,68,68,0.3)] disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center relative overflow-hidden"
              >
                {isLoading
                  ? <Loader2 className="h-6 w-6 animate-spin" />
                  : 'Sprawdź dostawę'}
              </button>
            </div>
          </m.div>
        ) : (
          <m.div
            key="confirm-step"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="space-y-4 lg:px-4"
          >
            {/* Address summary */}
            <div className="bg-white/40 backdrop-blur-md rounded-[1.5rem] p-6 border border-white/60 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.06)] relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-transparent pointer-events-none" />
              <div className="relative z-10">
                  <p className="text-[10px] font-black uppercase tracking-[0.25em] text-red-500 mb-2 drop-shadow-sm">Adres dostawy</p>
                  <p className="text-[22px] font-black text-black tracking-[-0.02em] leading-none mb-1">
                    {street} {building}{flat ? `/${flat}` : ''}
                  </p>
                  <p className="text-[14px] font-bold text-black/40">{postalCode} {city}</p>
              </div>
            </div>

            {/* Success */}
            {status === 'success' && (
              <div className="rounded-[1.5rem] border border-white bg-white/60 backdrop-blur-md overflow-hidden shadow-[0_12px_40px_-12px_rgba(34,197,94,0.15)] relative">
                <div className="flex items-center gap-4 p-5 bg-gradient-to-r from-green-500/10 to-transparent">
                  <div className="w-12 h-12 rounded-[0.85rem] bg-gradient-to-br from-green-400 to-green-500 flex items-center justify-center shrink-0 shadow-[0_4px_12px_rgba(34,197,94,0.3)]">
                    <CheckCircle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-black text-[16px] text-black leading-tight tracking-tight">Możemy dostarczyć!</h4>
                    <p className="text-[13px] text-green-700/80 font-bold mt-0.5 tracking-tight">Twój adres obejmuje strefa dostaw</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 divide-x divide-black/5 border-t border-black/5 bg-white/40">
                  <div className="p-5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-black/30 mb-1">Odległość</p>
                    <p className="text-[24px] font-black text-black tracking-[-0.03em] leading-none">{distanceKm} <span className="text-[14px] font-black text-black/30">km</span></p>
                  </div>
                  <div className="p-5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mb-1">Koszt</p>
                    <p className="text-[24px] font-black text-red-600 tracking-[-0.03em] leading-none">{cost} <span className="text-[14px] font-black text-red-600/50">zł</span></p>
                  </div>
                </div>
                <p className="px-5 pb-4 text-[11px] text-black/40 font-semibold tracking-tight text-center bg-white/40">
                  * Wyliczono na podstawie trasy przejazdu kuriera
                </p>
                <div className="px-5 pb-5 bg-white/40">
                  <button
                    onClick={handleFinalConfirm}
                    className="w-full h-14 bg-black text-white text-[13px] font-black uppercase tracking-[0.14em] rounded-[1rem] hover:bg-black/80 hover:scale-[1.01] active:scale-[0.98] transition-all duration-300 shadow-[0_12px_30px_rgba(0,0,0,0.2)] flex items-center justify-center gap-2 relative overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none" />
                    Akceptuję, zamawiam
                  </button>
                </div>
              </div>
            )}

            {/* Error */}
            {status === 'error' && (
              <div className="rounded-[1.5rem] border border-white bg-white/60 backdrop-blur-md overflow-hidden shadow-[0_12px_40px_-12px_rgba(239,68,68,0.15)]">
                <div className="flex items-center gap-4 p-5 bg-gradient-to-r from-red-500/10 to-transparent border-b border-black/5">
                  <div className="w-12 h-12 rounded-[0.85rem] bg-gradient-to-br from-red-400 to-red-500 flex items-center justify-center shrink-0 shadow-[0_4px_12px_rgba(239,68,68,0.3)]">
                    <XCircle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-black text-[16px] text-red-900 leading-tight">Brak dostawy</h4>
                    <p className="text-[13px] text-red-700/80 font-bold mt-0.5 tracking-tight">Nie dostarczamy w te okolice</p>
                  </div>
                </div>
                <div className="p-5 text-[13px] text-red-900/60 font-semibold leading-relaxed bg-white/40">
                  {errorMessage || 'Ten adres jest poza maksymalnym zasięgiem dla wybranej formy dostawy.'}
                  {distanceKm !== null && (
                    <div className="mt-3 inline-block px-3 py-1 rounded-md bg-red-50 border border-red-100 text-[11px] font-black uppercase tracking-widest text-red-500">
                      Odległość: {distanceKm} km
                    </div>
                  )}
                </div>
                <p className="px-5 pb-5 pt-1 text-[12px] text-black/50 font-semibold tracking-tight text-center bg-white/40">
                  Wybierz proszę <b className="text-black">odbiór osobisty</b> lub <b className="text-black">Sushimat</b>.
                </p>
              </div>
            )}

            {/* Back to edit */}
            <button
              onClick={() => setStep('input')}
              className="w-full flex items-center justify-center gap-2 py-3 text-[13px] font-black text-black/30 hover:text-black transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Edytuj adres
            </button>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  )
}
